import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useChatStore } from '@/store';
import {
  clampFloatEntryPosition,
  getFloatEntryPosition,
  saveFloatEntryPosition,
  type FloatEntryPosition,
} from '@/utils/floatEntryPosition';

const DRAG_THRESHOLD_PX = 5;

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  originLeft: number;
  originTop: number;
  moved: boolean;
}

const EMPTY_DRAG_STATE: DragState = {
  pointerId: -1,
  startX: 0,
  startY: 0,
  originLeft: 0,
  originTop: 0,
  moved: false,
};

export function useFloatEntryDrag(entryRef: RefObject<HTMLDivElement | null>) {
  const { identityUid, historyScope } = useChatStore();
  const [position, setPosition] = useState<FloatEntryPosition | null>(() => getFloatEntryPosition());
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef<DragState>(EMPTY_DRAG_STATE);
  const positionRef = useRef<FloatEntryPosition | null>(position);
  const suppressClickRef = useRef(false);

  positionRef.current = position;

  useEffect(() => {
    setPosition(getFloatEntryPosition());
  }, [identityUid, historyScope]);

  useEffect(() => {
    if (!position || !entryRef.current) return;

    const clamped = clampFloatEntryPosition(entryRef.current, position);
    if (clamped.left === position.left && clamped.top === position.top) return;

    setPosition(clamped);
    saveFloatEntryPosition(clamped);
  }, [entryRef, position]);

  useEffect(() => {
    const onResize = () => {
      const el = entryRef.current;
      const current = positionRef.current;
      if (!el || !current) return;

      const clamped = clampFloatEntryPosition(el, current);
      setPosition(clamped);
      saveFloatEntryPosition(clamped);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [entryRef]);

  const resolveOrigin = useCallback((): FloatEntryPosition | null => {
    const el = entryRef.current;
    if (!el) return null;

    if (positionRef.current) return positionRef.current;

    const rect = el.getBoundingClientRect();
    return { left: rect.left, top: rect.top };
  }, [entryRef]);

  const handleDragPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.button !== 0) return;

      const origin = resolveOrigin();
      if (!origin) return;

      if (!positionRef.current) {
        setPosition(origin);
      }

      dragState.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originLeft: origin.left,
        originTop: origin.top,
        moved: false,
      };

      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [resolveOrigin],
  );

  const handleDragPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const ds = dragState.current;
      if (ds.pointerId !== e.pointerId) return;

      const dx = e.clientX - ds.startX;
      const dy = e.clientY - ds.startY;
      if (!ds.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

      ds.moved = true;
      setIsDragging(true);

      const el = entryRef.current;
      if (!el) return;

      const next = clampFloatEntryPosition(el, {
        left: ds.originLeft + dx,
        top: ds.originTop + dy,
      });
      setPosition(next);
    },
    [entryRef],
  );

  const finishDrag = useCallback(() => {
    const ds = dragState.current;
    if (ds.moved) {
      if (positionRef.current) {
        saveFloatEntryPosition(positionRef.current);
      }
      suppressClickRef.current = true;
    }
    setIsDragging(false);
    dragState.current = EMPTY_DRAG_STATE;
  }, []);

  const handleDragPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const ds = dragState.current;
      if (ds.pointerId !== e.pointerId) return;

      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      finishDrag();
    },
    [finishDrag],
  );

  const handleDragPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      finishDrag();
    },
    [finishDrag],
  );

  const shouldSuppressClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  const positionStyle: React.CSSProperties | undefined = position
    ? { left: position.left, top: position.top }
    : undefined;

  return {
    positionStyle,
    isDragging,
    hasCustomPosition: position !== null,
    handleDragPointerDown,
    handleDragPointerMove,
    handleDragPointerUp,
    handleDragPointerCancel,
    shouldSuppressClick,
  };
}
